/**
 * Markup style persistence utility
 * Saves and loads markup style preferences and selected tool from browser localStorage
 */

import type { ShapeStyle, ShapeType } from '@/types/markup.types';
import { DEFAULT_SHAPE_STYLE } from '@/types/markup.types';
import { getToolMetadata } from '@/utils/markupToolRegistry';
import logger from '@/utils/logger';

const STORAGE_KEY = 'markup_style_preferences';
const TOOL_STORAGE_KEY = 'markup_selected_tool';

/**
 * Load saved markup style preferences from localStorage
 * Returns DEFAULT_SHAPE_STYLE if no saved preferences exist or if parsing fails
 */
export function loadMarkupStylePreferences(): ShapeStyle {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      logger.debug('MarkupStylePersistence', 'No saved preferences found, using defaults');
      return DEFAULT_SHAPE_STYLE;
    }

    const parsed = JSON.parse(saved) as ShapeStyle;

    // Validate that parsed data has required fields
    if (
      typeof parsed.strokeWidth === 'number' &&
      typeof parsed.strokeColor === 'string' &&
      typeof parsed.strokeDasharray === 'string'
    ) {
      logger.info('MarkupStylePersistence', 'Loaded saved preferences:', parsed);
      return parsed;
    }

    logger.warn('MarkupStylePersistence', 'Invalid saved data, using defaults');
    return DEFAULT_SHAPE_STYLE;
  } catch (error) {
    logger.error('MarkupStylePersistence', 'Failed to load preferences:', error);
    return DEFAULT_SHAPE_STYLE;
  }
}

/**
 * Save markup style preferences to localStorage
 */
export function saveMarkupStylePreferences(style: ShapeStyle): void {
  try {
    const serialized = JSON.stringify(style);
    localStorage.setItem(STORAGE_KEY, serialized);
    logger.debug('MarkupStylePersistence', 'Saved preferences:', style);
  } catch (error) {
    logger.error('MarkupStylePersistence', 'Failed to save preferences:', error);
  }
}

/**
 * Clear saved markup style preferences
 */
export function clearMarkupStylePreferences(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    logger.info('MarkupStylePersistence', 'Cleared saved preferences');
  } catch (error) {
    logger.error('MarkupStylePersistence', 'Failed to clear preferences:', error);
  }
}

/**
 * Load saved selected tool from localStorage
 * Returns null if no saved tool exists or if parsing fails
 */
export function loadSelectedTool(): ShapeType | null {
  try {
    const saved = localStorage.getItem(TOOL_STORAGE_KEY);
    if (!saved) {
      logger.debug('MarkupStylePersistence', 'No saved tool found');
      return null;
    }

    const parsed = JSON.parse(saved) as ShapeType | null;

    // Validate that it's a valid tool type (null or registered in tool registry)
    if (parsed === null) {
      logger.info('MarkupStylePersistence', 'Loaded saved tool:', parsed);
      return null;
    }

    // Check if tool is registered in the tool registry
    const toolMetadata = getToolMetadata(parsed);
    if (toolMetadata) {
      logger.info('MarkupStylePersistence', 'Loaded saved tool:', parsed);
      return parsed;
    }

    logger.warn('MarkupStylePersistence', 'Invalid saved tool (not registered):', parsed);
    return null;
  } catch (error) {
    logger.error('MarkupStylePersistence', 'Failed to load tool:', error);
    return null;
  }
}

/**
 * Save selected tool to localStorage
 */
export function saveSelectedTool(tool: ShapeType | null): void {
  try {
    const serialized = JSON.stringify(tool);
    localStorage.setItem(TOOL_STORAGE_KEY, serialized);
    logger.debug('MarkupStylePersistence', 'Saved tool:', tool);
  } catch (error) {
    logger.error('MarkupStylePersistence', 'Failed to save tool:', error);
  }
}
